import { colors } from './colors';
import useWindowDimensions from '../utils/hooks/useWindowDimensions';
import { getByScreenSize, hdp, wdp } from '../utils/responsive';

const height = window.innerHeight;

const lightTheme = () => {
  const { isMobile } = useWindowDimensions();
  return {
    primary: colors.primary,
    secondary: colors.secondary,
    homeBackground: '#FFFFFF',
    primaryOverlay: 'rgba(26, 115, 232, 0.1)',
    border: '#E0E0E0',
    shadow: 'rgba(0, 0, 0, 0.1)',
    ripple: colors.ripple || 'rgba(26, 115, 232, 0.2)',
    skeletonBackground: '#aeaeae',
    overlay: 'rgba(0, 0, 0, 0.05)',
    overlayScreen: 'rgba(0, 0, 0, 0.2)',
    defaultOverlayScreen: 'rgba(0, 0, 0, 0.05)',
    card: {
      background: 'linear-gradient(135deg, #FFFFFF 0%, #F8FAFF 100%)',
      backgroundHover: 'linear-gradient(135deg, #F8FAFF 0%, #F0F4FF 100%)',
      border: 'rgba(0, 0, 102, 0.1)',
      text: '#4A5568',
      hoverEffect: 'rgba(40, 35, 245, 0.2)',
    },
    dimensions: {
      hdp: (percent: number) => hdp(percent),
      wdp: (percent: number) => wdp(percent),
      getByScreenSize: (smValue: any, lgValue: any, xsmValue: any = smValue) =>
        getByScreenSize(smValue, lgValue, xsmValue),
    },
    isMobile,
    alert: {
      backgroundPrimary: colors.primary || '#1A73E8',
      textPrimary: '#FFFFFF',
    },
    fonts: {
      en: '"Helvetica", Arial, sans-serif',
    },
    text: {
      dark: colors.title || '#fff',
      black: '#202124',
      border: '#E0E0E0',
      icon: colors.icon || '#5F6368',
      error: colors.error || '#D93025',
      grey: colors.description || '#5F6368',
      optionIcon: '#646460',
      shine: '#000000',
      light: '#000000',
      title: colors.title || '#202124',
      description: colors.description || '#5F6368',
      textPrimary: '#202124',
      placeholder: '#5F6368',
      borderLight: '#FFFFFF',
      borderFocus: '#1A73E8',
      tabsTitle: '#000000',
      s1: height >= 900 ? '4.5rem' : '4rem',
      s2: height >= 900 ? '4rem' : '3.25rem',
      s25: height >= 900 ? '3.5rem' : '2.5rem',
      s3: height >= 900 ? '2.75rem' : '2rem',
      s35: height >= 900 ? '1.8rem' : '1.4rem',
      s4: height >= 900 ? '1.5rem' : '1.2rem',
      s5: height >= 900 ? '1.4rem' : '1rem',
      s55: height >= 900 ? '1.2rem' : '0.8rem',
      s6: height >= 900 ? '1.2rem' : '0.8rem',
      s7: height >= 900 ? '1rem' : '0.9rem',
      s8: height >= 900 ? '0.875rem' : '0.8rem',
      s85: height >= 900 ? '0.875rem' : '0.7rem',
      s89: height >= 900 ? '0.825rem' : '0.675rem',
      s9: height >= 900 ? '0.8rem' : '0.65rem',
      s10: height >= 900 ? '0.7rem' : '0.6rem',
      s11: height >= 900 ? '0.65rem' : '0.55rem',
      s12: height >= 900 ? '0.5rem' : '0.4rem',
    },
    button: {
      text: '#FFFFFF',
      lightColor: '#FFFFFF',
      outline: colors.primary || '#1A73E8',
    },
    textInput: {
      cursorColor: colors.primary || '#1A73E8',
      placeholder: '#A0A0A0',
      border: '#E0E0E0',
      required: '#D93025',
    },
    drawer: {
      bgColor1: (colors.primary || '#1A73E8') + 'AA',
      bgColor2: (colors.secondary || '#4285F4') + 'AA',
      fontColor: '#FFFFFF',
      fontColorSelected: '#F1F3F4',
      dividerColor: '#E0E0E0',
      selectedBackgroundCollapsedMode: 'light',
    },
    voiceQuestionInput: {
      text: '#202124',
      background: '#f3f3ef',
      inputPlaceholder: '#646460',
      iosEndColor: 'rgba(0,0,0,0)',
      mainButtonStartColor: '#f0f0ec',
      mainButtonEndColor: colors.primary || '#1A73E8',
      bubbleBackground: '#f1f1ed',
    },
    afterLogin: {
      contentBackground: '#F8F9FA',
      text: '#202124',
      label: '#5F6368',
      line: '#E0E0E0',
      selected: 'rgba(26,115,232,0.1)',
      checkIcon: '#FFFFFF',
      checkIconBackground: colors.primary || '#1A73E8',
    },
    scrollbar: {
      track: 'transparent',
      thumb: 'rgba(26,115,232,0.5)',
      thumbHover: 'rgba(26,115,232,0.7)',
    },
    chatList: {
      text: '#1c1c1c',
    },
    sidebar: {
      background: '#f3f3ef',
      shadow: '#f3f3ef',
      hover: '#f3f3ef',
    },
    tooltip: {
      background: '#f3f3ef',
      grayText: 'rgba(0,0,0,0.2)',
      text: '#000000',
      descriptionText: '#535353',
      border: '#E0E0E0',
      shadow: 'rgba(0, 0, 0, 0.1)',
      highlight: '#1A73E8',
    },
    ai: {
      color1: '#7F56D9',
      color2: '#3B82F6',
      color3: '#10B981',
    },
    table: {
      thead: '#f7f7f7',
      text: '#333333',
      tr: '#f2f2f2',
      trHover: '#e6f7ff',
      evenRow: '#f9f9f9',
    },
    link: {
      background: 'rgba(135,135,135,0.8)',
    },
    chatBottomBar: {
      sendIcon: '#ffffff',
      background: colors.primary,
    },
    dropdown: {
      background: '#ffffff',
      color: '#e3e3e3',
      delete: '#f93a37',
    },
    tabs: {
      cardTitle: colors.primary,
      cardDescription: colors.description,
    },
    categoryBullet: {
      text: '#333333',
      background: '#f5f5f5',
      selectedText: colors.primary,
      selectedBackground: `${colors.primary}15`,
      selectedBorder: colors.primary,
      hoverBackground: '#e8e8e8',
      selectedHoverBackground: `${colors.primary}25`,
    },
  };
};

/**
 * The type representing the structure of the light theme.
 * @type {Theme}
 */
type Theme = ReturnType<typeof lightTheme>;

export type { Theme };
export default lightTheme;
